import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DeclarationType, ProcessStatus, FactProcess } from '../types';
import { CaseDatabase } from '../services/db';

const NewProcessForm: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
    // Helper to get the current date with default time at 9:00 AM
  const getCurrentDate = () => {
    const now = new Date();
    now.setHours(9, 0, 0, 0);
    return now;
  }
  /**
   * Robustly parses date strings from various formats.
   * Prioritizes ISO (datetime-local default) and falls back to manual entry formats.
   */
  const parseInputDate = (dateStr: string): Date => {
    console.log('[Date Debug] Attempting to parse:', dateStr);
    if (!dateStr) return new Date(NaN);
    
    // 1. Try standard ISO parsing (YYYY-MM-DDTHH:mm) - standard for datetime-local
    let d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      console.log('[Date Debug] Successfully parsed via ISO:', d.toISOString());
      return d;
    }

    // 2. Fallback to 'DD/MM/YYYY HH:mm am/pm' format
    const regex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s*(am|pm)?$/i;
    const match = dateStr.match(regex);
    if (match) {
      const [_, day, month, year, hours, minutes, ampm] = match;
      let h = parseInt(hours, 10);
      if (ampm) {
        const ampmLower = ampm.toLowerCase();
        if (ampmLower === 'pm' && h < 12) h += 12;
        if (ampmLower === 'am' && h === 12) h = 0;
      }
      d = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10), h, parseInt(minutes, 10));
      console.log('[Date Debug] Successfully parsed via Manual Format:', d.toISOString());
      return d;
    }
    
    console.log('[Date Debug] All parsing methods failed for string:', dateStr);
    return new Date(NaN);
  };

  // Helper to get formatted local string for datetime-local input (YYYY-MM-DDTHH:mm)
  const getNowString = () => {
    const now = getCurrentDate();
    // Round to next hour
    now.setHours(now.getHours() + 1);
    now.setMinutes(0);
    
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    language: 'English',
    declarationType: DeclarationType.ASYLUM,
    focusDetails: '',
    appointmentDateTime: getNowString(),
    method: 'Telephonic' as 'Telephonic' | 'Text'
  });

  const [dateError, setDateError] = useState('');

  // Clear date error automatically and immediately when a valid date is selected
  useEffect(() => {
    const selectedDate = parseInputDate(formData.appointmentDateTime);
    const threshold = getCurrentDate();
    
    if (!isNaN(selectedDate.getTime()) && selectedDate >= threshold) {
      console.log('[Date Debug] Valid future date detected, clearing error.');
      setDateError('');
    } else if (!isNaN(selectedDate.getTime()) && selectedDate < threshold) {
      console.log('[Date Debug] Date is valid but in the past.');
      setDateError('Consultation time cannot be in the past.');
    }
  }, [formData.appointmentDateTime]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setDateError('');

    const selectedDate = parseInputDate(formData.appointmentDateTime);
    const threshold = getCurrentDate();

    if (isNaN(selectedDate.getTime())) {
      setDateError('Please select a valid date and time.');
      return;
    }
    
    if (selectedDate < threshold) {
      setDateError('Consultation time cannot be in the past.');
      return;
    }

    setLoading(true);

    const now = getCurrentDate().toISOString();
    const newIntake: FactProcess = {
      id: crypto.randomUUID(), 
      type: formData.declarationType,
      client: {
        id: crypto.randomUUID(),
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        language: formData.language,
        focusDetails: formData.focusDetails
      },
      appointment: {
        dateTime: selectedDate.toISOString(),
        method: formData.method
      },
      status: ProcessStatus.PENDING,
      notifications: {
        reminder24hSent: false,
        reminder15mSent: false
      },
      transcript: [],
      createdAt: now,
      lastUpdated: now
    };

    try {
      await CaseDatabase.save(newIntake);
      setTimeout(() => {
        setLoading(false);
        navigate('/');
      }, 500);
    } catch (error) {
      console.error("Failed to save to database:", error);
      setLoading(false);
      alert("Error saving case to database. Please try again.");
    }
  };

  const SectionHeader = ({ title, icon }: { title: string, icon: string }) => (
    <div className="flex items-center gap-4 mb-8">
      <div className="w-10 h-10 rounded-2xl bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 flex items-center justify-center shadow-md">
        <i className={`fas ${icon} text-sm`}></i>
      </div>
      <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-[0.2em]">{title}</h4>
      <div className="flex-1 h-[1px] bg-slate-100 dark:bg-slate-800"></div>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto pb-20">
      <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-[0_32px_80px_-20px_rgba(0,0,0,0.08)] border border-slate-100 dark:border-slate-800 overflow-hidden transition-colors">
        <div className="px-12 py-12 bg-white dark:bg-slate-900 flex flex-col md:flex-row md:items-center justify-between border-b border-slate-50 dark:border-slate-800 transition-colors">
          <div>
            <h3 className="text-4xl font-serif font-bold text-slate-900 dark:text-slate-100 leading-tight">New Intake Initiation</h3>
            <p className="text-slate-400 dark:text-slate-500 mt-2 font-medium text-lg">Initialize a secure client file and schedule the AI consultant.</p>
          </div>
          <div className="mt-8 md:mt-0 p-8 bg-slate-50 dark:bg-slate-950/50 rounded-3xl border border-slate-100 dark:border-slate-800 hidden lg:flex flex-col items-center justify-center text-center transition-colors">
            <span className="text-2xl font-serif font-bold text-amber-600 dark:text-amber-500 leading-tight">Lluis Law</span>
            <span className="text-[8px] font-sans font-black uppercase tracking-[0.4em] text-slate-400 dark:text-slate-600 mt-1">Fact Gather</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-12 space-y-16">
          {/* Client Details */}
          <section>
            <SectionHeader title="Client Profile" icon="fa-user-tie" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
              <div className="space-y-2">
                <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-widest ml-1">Legal Name</label>
                <input 
                  type="text" 
                  required
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  className="w-full px-6 py-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 focus:bg-white dark:focus:bg-slate-900 focus:border-slate-900 dark:focus:border-amber-600 focus:ring-4 focus:ring-slate-900/5 dark:focus:ring-amber-600/5 outline-none transition-all font-semibold placeholder-slate-300 dark:placeholder-slate-700"
                  placeholder="Full legal name"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-widest ml-1">Primary Language</label>
                <select 
                  className="w-full px-6 py-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 focus:bg-white dark:focus:bg-slate-900 focus:border-slate-900 dark:focus:border-amber-600 focus:ring-4 focus:ring-slate-900/5 dark:focus:ring-amber-600/5 outline-none transition-all font-semibold appearance-none cursor-pointer"
                  value={formData.language}
                  onChange={e => setFormData({...formData, language: e.target.value})}
                >
                  <option>English</option>
                  <option>Spanish</option>
                  <option>French</option>
                  <option>Chinese</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-widest ml-1">Mobile Contact</label>
                <input 
                  type="tel" 
                  required
                  value={formData.phone}
                  onChange={e => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-6 py-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 focus:bg-white dark:focus:bg-slate-900 focus:border-slate-900 dark:focus:border-amber-600 focus:ring-4 focus:ring-slate-900/5 dark:focus:ring-amber-600/5 outline-none transition-all font-semibold placeholder-slate-300 dark:placeholder-slate-700"
                  placeholder="+1 (555) 000-0000"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-widest ml-1">Email Address</label>
                <input 
                  type="email" 
                  required
                  value={formData.email}
                  onChange={e => setFormData({...formData, email: e.target.value})}
                  className="w-full px-6 py-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 focus:bg-white dark:focus:bg-slate-900 focus:border-slate-900 dark:focus:border-amber-600 focus:ring-4 focus:ring-slate-900/5 dark:focus:ring-amber-600/5 outline-none transition-all font-semibold placeholder-slate-300 dark:placeholder-slate-700"
                  placeholder="client@email.com"
                />
              </div>
            </div>
          </section>

          {/* Case Details */}
          <section>
            <SectionHeader title="Matter Strategy" icon="fa-scale-unbalanced" />
            <div className="space-y-10">
              <div className="space-y-4">
                <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-widest ml-1">Declaration Type</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.values(DeclarationType).map(type => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setFormData({...formData, declarationType: type})}
                      className={`px-6 py-5 rounded-2xl border-2 text-sm font-bold transition-all text-left flex items-center justify-between ${
                        formData.declarationType === type 
                          ? 'border-slate-900 dark:border-white bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-xl dark:shadow-none' 
                          : 'border-slate-100 dark:border-slate-800 text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-950 hover:border-slate-300 dark:hover:border-slate-700'
                      }`}
                    >
                      {type}
                      {formData.declarationType === type && <i className="fas fa-check text-amber-500"></i>}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-widest ml-1">Strategic Instructions for AI</label>
                <textarea 
                  rows={4}
                  value={formData.focusDetails}
                  onChange={e => setFormData({...formData, focusDetails: e.target.value})}
                  className="w-full px-8 py-6 rounded-[2rem] border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 focus:bg-white dark:focus:bg-slate-900 focus:border-slate-900 dark:focus:border-amber-600 focus:ring-4 focus:ring-slate-900/5 dark:focus:ring-amber-600/5 outline-none transition-all font-medium placeholder-slate-300 dark:placeholder-slate-700 leading-relaxed"
                  placeholder="Detail specific elements for Victoria to probe (e.g., specific dates of persecution, social group definitions)..."
                ></textarea>
                <p className="text-xs text-slate-400 dark:text-slate-600 font-medium italic mt-2">These internal notes direct the AI's questioning strategy and legal framing.</p>
              </div>
            </div>
          </section>

          {/* Appointment */}
          <section>
            <SectionHeader title="Logistics" icon="fa-clock" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
              <div className="space-y-2">
                <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-widest mb-1 ml-1">Proposed Consultation Time</label>
                <input 
                  type="datetime-local" 
                  required
                  min={getNowString()}
                  value={formData.appointmentDateTime}
                  onChange={e => setFormData({...formData, appointmentDateTime: e.target.value})}
                  className={`w-full px-6 py-4 rounded-2xl border bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 outline-none transition-all font-semibold dark:scheme-dark ${
                    dateError ? 'border-red-500 ring-4 ring-red-500/10' : 'border-slate-200 dark:border-slate-800 focus:bg-white dark:focus:bg-slate-900 focus:border-slate-900 dark:focus:border-amber-600'
                  }`}
                />
                {dateError && <p className="text-red-500 text-[10px] font-bold uppercase tracking-wider mt-2 ml-1 animate-in slide-in-from-left-2">{dateError}</p>}
              </div>
              <div className="space-y-2">
                <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-widest ml-1">Interface Delivery</label>
                <div className="flex gap-4">
                  <label className="flex-1 group cursor-pointer">
                    <input 
                      type="radio" 
                      name="method" 
                      className="hidden peer" 
                      checked={formData.method === 'Telephonic'}
                      onChange={() => setFormData({...formData, method: 'Telephonic'})}
                    />
                    <div className="text-center px-6 py-5 rounded-2xl border-2 border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 peer-checked:border-slate-900 dark:peer-checked:border-white peer-checked:bg-white dark:peer-checked:bg-slate-900 peer-checked:shadow-xl dark:peer-checked:shadow-none transition-all group-hover:border-slate-300 dark:group-hover:border-slate-700">
                      <i className="fas fa-microphone-lines mb-2 block text-2xl text-slate-400 dark:text-slate-600 peer-checked:text-amber-600 dark:peer-checked:text-amber-500"></i>
                      <span className="font-bold text-sm text-slate-800 dark:text-slate-200">Voice Interface</span>
                    </div>
                  </label>
                  <label className="flex-1 group cursor-pointer">
                    <input 
                      type="radio" 
                      name="method" 
                      className="hidden peer"
                      checked={formData.method === 'Text'}
                      onChange={() => setFormData({...formData, method: 'Text'})}
                    />
                    <div className="text-center px-6 py-5 rounded-2xl border-2 border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 peer-checked:border-slate-900 dark:peer-checked:border-white peer-checked:bg-white dark:peer-checked:bg-slate-900 peer-checked:shadow-xl dark:peer-checked:shadow-none transition-all group-hover:border-slate-300 dark:group-hover:border-slate-700">
                      <i className="fas fa-keyboard mb-2 block text-2xl text-slate-400 dark:text-slate-600 peer-checked:text-amber-600 dark:peer-checked:text-amber-500"></i>
                      <span className="font-bold text-sm text-slate-800 dark:text-slate-200">Text Protocol</span>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </section>

          <div className="pt-12 flex flex-col sm:flex-row justify-end gap-6">
            <button 
              type="button"
              onClick={() => navigate('/')}
              className="px-10 py-5 rounded-2xl text-slate-400 dark:text-slate-500 font-bold hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100 transition-all uppercase text-xs tracking-widest"
            >
              Cancel Intake
            </button>
            <button 
              type="submit"
              disabled={loading}
              className="px-12 py-5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-2xl font-bold hover:bg-slate-800 dark:hover:bg-slate-200 shadow-2xl shadow-slate-300 dark:shadow-none transition-all disabled:opacity-50 flex items-center justify-center gap-4 uppercase text-xs tracking-widest"
            >
              {loading ? <i className="fas fa-circle-notch animate-spin"></i> : <i className="fas fa-plus"></i>}
              Initialize Matter
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewProcessForm;
